import { useState } from 'react';
import type { FormEvent } from 'react';
import { LoginDidNotHoldError, signIn } from '../lib/auth.ts';
import { ApiError, asSentence } from '../lib/http.ts';
import { AuthCard } from '../components/AuthCard.tsx';
import { Field } from '../components/Field.tsx';

const THROTTLED = 'Too many failed attempts for this email. Try again in 15 minutes.';

/**
 * What a refused login says. A 401 gives one answer for an unknown email and a wrong password,
 * because the server does (docs/specs/accounts.md, section 2). A 429 uses the server's words when
 * it sent any, because only the server knows how long is left. The fixed copy states the window
 * from the same section. A 400 names the missing field, so it also uses the server's words.
 */
export function loginErrorText(e: unknown): string {
  if (e instanceof LoginDidNotHoldError) return e.message;
  if (e instanceof ApiError) {
    if (e.status === 401) return 'Wrong email or password.';
    if (e.status === 429) return e.serverMessage ? asSentence(e.serverMessage) : THROTTLED;
    if (e.serverMessage) return asSentence(e.serverMessage);
  }
  return `Sign-in failed: ${e instanceof Error ? e.message : String(e)}`;
}

/** `#/login`. `returnTo` is the hash a 401 interrupted; a successful login goes back to it. */
export function LoginView({ returnTo }: { returnTo: string | null }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password, returnTo);
      // Success leaves this route, so there is no state left to set.
    } catch (e) {
      setError(loginErrorText(e));
      setPassword('');
      setBusy(false);
      document.getElementById('login-password')?.focus();
    }
  }

  return (
    <AuthCard
      title="Sign in"
      footnote="Accounts are made by invitation. If you forgot your password, ask the admin for a new setup link."
    >
      <form className="form" noValidate onSubmit={(e) => void onSubmit(e)}>
        <Field
          id="login-email"
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          id="login-password"
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p className="notice err" role="alert">
            {error}
          </p>
        )}
        <button className="btn primary block" type="submit" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthCard>
  );
}
